// src/components/layout/AppLayout.jsx
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useTheme } from "../../context/ThemeContext";
import { logout } from "../../services/authService";
import {
  Gauge, Briefcase, CircleUser, Wallet,
  SlidersHorizontal, MapPin, LogOut, Sun, Moon, CreditCard,
} from "lucide-react";
import styles from "./AppLayout.module.css";

const NAV_LINKS = [
  { path: "/dashboard",    label: "Dashboard",   icon: Gauge       },
  { path: "/jobs",         label: "Browse Jobs", icon: Briefcase   },
  { path: "/profile",      label: "My Profile",  icon: CircleUser  },
  { path: "/credits",      label: "Credits",     icon: Wallet      },
];

const ADMIN_LINKS = [
  { path: "/admin/dashboard",  label: "Admin Portal", icon: SlidersHorizontal },
  { path: "/admin/local-gigs", label: "Local Gigs",   icon: MapPin            },
];

export default function AppLayout({ children }) {
  const { currentUser, userProfile, userRole, credits } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + "/");

  return (
    <div className={styles.shell}>
      {/* ── Sidebar ── */}
      <aside className={styles.sidebar}>
        <div className={styles.sidebarTop}>
          {/* Brand */}
          <button className={styles.brand} onClick={() => navigate("/dashboard")}>
            <span className={styles.brandLogo}>GS</span>
            <span className={styles.brandName}>Gigscout</span>
          </button>

          {/* Main nav */}
          <nav className={styles.nav}>
            {NAV_LINKS.map(({ path, label, icon: Icon }) => (
              <button
                key={path}
                className={`${styles.navItem} ${isActive(path) ? styles.navItemActive : ""}`}
                onClick={() => navigate(path)}
              >
                <span className={styles.navIcon}><Icon size={19} strokeWidth={2} /></span>
                <span className={styles.navLabel}>{label}</span>
                {path === "/credits" && (
                  <span className={`${styles.creditBadge} ${credits <= 2 ? styles.creditBadgeLow : ""}`}>{credits}</span>
                )}
              </button>
            ))}
          </nav>

          {/* Admin section */}
          {userRole === "admin" && (
            <div className={styles.navSection}>
              <span className={styles.navSectionLabel}>Admin</span>
              {ADMIN_LINKS.map(({ path, label, icon: Icon }) => (
                <button
                  key={path}
                  className={`${styles.navItem} ${isActive(path) ? styles.navItemActive : ""}`}
                  onClick={() => navigate(path)}
                >
                  <span className={styles.navIcon}><Icon size={19} strokeWidth={2} /></span>
                  <span className={styles.navLabel}>{label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Credits card */}
        <div className={`${styles.creditCard} ${credits <= 2 ? styles.creditCardLow : ""}`}>
          <div className={styles.creditCardTop}>
            <span className={styles.creditCardLabel}>
              {credits <= 2 ? "⚠ Low credits" : "Credits"}
            </span>
            <span className={`${styles.creditCardValue} ${credits <= 2 ? styles.creditCardValueLow : ""}`}>{credits}</span>
          </div>
          <div className={styles.creditCardBar}>
            <div
              className={`${styles.creditCardFill} ${credits <= 2 ? styles.creditCardFillLow : ""}`}
              style={{ width: `${Math.min(100, (credits / 30) * 100)}%` }}
            />
          </div>
          <button className={styles.creditCardBtn} onClick={() => navigate("/credits")}>
            {credits <= 2 ? "Top up now →" : "Buy more credits →"}
          </button>
        </div>

        {/* User card */}
        <div className={styles.userCard}>
          <div className={styles.userAvatar}>
            {userProfile?.photoURL
              ? <img src={userProfile.photoURL} alt="" />
              : <span>{(userProfile?.displayName || currentUser?.email || "?")[0].toUpperCase()}</span>
            }
          </div>
          <div className={styles.userInfo}>
            <span className={styles.userName}>
              {userProfile?.displayName || "Set your name"}
            </span>
            <span className={styles.userRole}>{userRole}</span>
          </div>
          <button
            className={styles.themeToggle}
            onClick={toggleTheme}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          >
            {theme === "dark"
              ? <Sun size={15} strokeWidth={2} />
              : <Moon size={15} strokeWidth={2} />
            }
          </button>
          <button className={styles.logoutBtn} onClick={handleLogout} title="Sign out">
            <LogOut size={15} strokeWidth={2} />
          </button>
        </div>
      </aside>

      {/* ── Content ── */}
      <div className={styles.content}>
        {/* Mobile top bar */}
        <header className={styles.mobileBar}>
          <button className={styles.brand} onClick={() => navigate("/dashboard")}>
            <span className={styles.brandLogo}>GS</span>
            <span className={styles.brandName}>Gigscout</span>
          </button>
          <div className={styles.mobileRight}>
            <button className={styles.themeToggle} onClick={toggleTheme}>
              {theme === "dark"
                ? <Sun size={16} strokeWidth={2} />
                : <Moon size={16} strokeWidth={2} />
              }
            </button>
            <button
              className={`${styles.mobileCreditBtn} ${credits <= 2 ? styles.mobileCreditBtnLow : ""}`}
              onClick={() => navigate("/credits")}
            >
              <CreditCard size={14} strokeWidth={2} /> {credits <= 2 ? `⚠ ${credits}` : credits}
            </button>
            <button className={styles.mobileAvatar} onClick={() => navigate("/profile")}>
              {userProfile?.photoURL
                ? <img src={userProfile.photoURL} alt="" />
                : <span>{(userProfile?.displayName || "?")[0].toUpperCase()}</span>
              }
            </button>
            <button className={styles.mobileLogout} onClick={handleLogout} title="Sign out">
              <LogOut size={16} strokeWidth={2} />
            </button>
          </div>
        </header>

        {/* Mobile bottom nav */}
        <nav className={styles.mobileNav}>
          {NAV_LINKS.map(({ path, label, icon: Icon }) => (
            <button
              key={path}
              className={`${styles.mobileNavItem} ${isActive(path) ? styles.mobileNavActive : ""}`}
              onClick={() => navigate(path)}
            >
              <Icon size={20} strokeWidth={2} />
              <span>{label}</span>
              {path === "/credits" && credits > 0 && (
                <span className={styles.mobileBadge}>{credits}</span>
              )}
            </button>
          ))}
        </nav>

        <main className={styles.main}>{children}</main>
      </div>
    </div>
  );
}
